<template>
    <v-container class="toggle animated" fluid>
        <v-row dense>
            <v-col cols="12">
                <v-tabs v-model="selectedTab" align-tabs="start" color="darkGreen" @update:modelValue="initUsers()">
                    <v-tab :class="{'font-weight-black': selectedTab == institute.id}" v-for="institute in instituteData" :key="institute.id" :value="institute.id">
                        {{ institute.abbrev }}
                    </v-tab>
                </v-tabs>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="search"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    label="Search"
                    density="comfortable"
                    solo
                    hide-details
                    rounded="lg"
                    color="primary"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
                <v-select
                    v-model="statusFilter"
                    :items="statusOptions"
                    label="Filter by Status"
                    variant="outlined"
                    dense
                    hide-details
                    density="comfortable"
                    solo
                    rounded="lg"
                    color="primary"
                ></v-select>
            </v-col>
            <v-col cols="12">
                <v-row dense>
                    <v-col cols="12">
                        <v-card class="dt-container mt-2">    
                            <v-data-table
                                :mobile="isMobileOrTablet()"
                                :headers="headers"
                                :items="filteredData"
                                :search="search"
                                :loading="loading"
                                :items-per-page="15"
                                :items-per-page-options="[15,30, 50, 100, -1]"
                            >
                            <template v-slot:loading>
                                <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                            </template>
                            <template v-slot:item.datecreated="{ item }">
                                <span>{{ formatDateTime(item.datecreated)}} </span>
                            </template>
                            <template v-slot:item.status="{ item }">
                                <v-chip :color="getWorkloadStatusColor(item)" class="text-caption">
                                    <span>{{ getWorkloadStatus(item) }}</span>
                                </v-chip>
                            </template>
                            <template v-slot:[`item.action`]="{ item }">
                                <div class="d-flex justify-end ga-2">
                                    <v-btn @click="showViewDialog(item)" size="small" variant="outlined" text="View" color="green" elevation="3"></v-btn>
                                </div>
                            </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>

    <WorkloadApprovalDialog ref="WorkloadApprovalDialog" @refresh="initUsers()"/>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import WorkloadApprovalDialog from '../Dialogs/WorkloadApprovalDialog.vue';

export default defineComponent({
    components: {
        WorkloadApprovalDialog
    },
    data(){
        return {
            data: [],
            search: null,
            loading: false,
            statusFilter: 'All',
            headers: [
                { title: 'Faculty Name', align: 'start', sortable: true, key: 'faculty_name' },
                { title: 'Program/Department', align: 'start', sortable: true, key: 'department' },
                { title: 'Dated Created', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Status', align: 'start', sortable: true, key: 'status' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],


            instituteData: [],
            selectedTab: 0,
        }
    },
    computed: {
        filteredData() {
            let filtered = this.data;
            if (this.statusFilter && this.statusFilter !== 'All') {
                filtered = filtered.filter(item => this.getWorkloadStatus(item) === this.statusFilter);
            }
            // Sort so "For Approval" appears first
            return filtered.slice().sort((a, b) => {
                const statusA = this.getWorkloadStatus(a);
                const statusB = this.getWorkloadStatus(b);
                if (statusA === "For Approval" && statusB !== "For Approval") return -1;
                if (statusA !== "For Approval" && statusB === "For Approval") return 1;
                return 0;
            });
        },
        statusOptions() {
            const statuses = this.data.map(item => this.getWorkloadStatus(item));
            const uniqueStatuses = [...new Set(statuses)];
            return ['All', ...uniqueStatuses];
        }
    },
    methods: {
        async init(){
            await axios.get(process.env.VUE_APP_SERVER_URL + '/offices/getAllInstitute')
            .then(res => {
                this.instituteData = res.data
                this.selectedTab = this.instituteData[0].id
            });

            this.initUsers()
        },
        initUsers(){
            this.loading = true
            const token = localStorage.getItem('aums-token')
            axios.get(process.env.VUE_APP_SERVER_URL + '/faculty-workload/findAllPreApprovedByInstituteAndSemesterSchoolyear/' + this.selectedTab + '/' + this.$store.state.semester_schoolyear.id,
            { headers:
                { 
                    'Authorization': `Bearer ${token}`,
                }
            }
            ).then((res)=>{
                if(res.data){
                    this.loading = false
                    this.data = res.data
                }
            }).catch((error)=>{
                if(error.response.status == 401){
                    this.loading = false

                    localStorage.removeItem("vuex");
                    localStorage.removeItem("aums-token");
                    window.location.reload();
                }
                else{
                    console.log(error)
                }
            });
        },
        getWorkloadStatus(item) {
            if (!item.prepared_status) return "For Preparation";
            if (item.prepared_status === "prepared" && !item.recommending_approval_status) return "For Recommending Approval";
            if (item.recommending_approval_status === "recommended" && !item.pre_approved_status) return "For Pre-Approval";
            if (item.pre_approved_status === "pre-approved" && !item.approved_status) return "For Approval";
            if (item.approved_status === "approved") return "Approved";
            return "Unknown Status";
        },

        getWorkloadStatusColor(item) {
            if (!item.prepared_status) return "orange";
            if (item.prepared_status === "prepared" && !item.recommending_approval_status) return "blue";
            if (item.recommending_approval_status === "recommended" && !item.pre_approved_status) return "teal";
            if (item.pre_approved_status === "pre-approved" && !item.approved_status) return "purple";
            if (item.approved_status === "approved") return "green";
            return "gray";
        },
        showViewDialog(data){
            this.$refs.WorkloadApprovalDialog.initView(data)
        },
    },
    mounted(){
        this.init()
    }
})
</script>

<style scoped>

</style>